import { useFrame, useThree } from "@react-three/fiber";
import { useEffect, useRef } from "react";
import { hasPendingTextureWork } from "../../engines/materials";

/**
 * Publishes a deterministic "a complete WebGL frame has actually been
 * composited" signal for both the visual-baseline harness and the viewer's
 * initial loading UI.
 *
 * `data-test="viewer-ready"` on the <Canvas> only proves the element
 * mounted; `onRendererReady` only proves the renderer was created. Neither
 * guarantees the backing buffer holds a fully painted scene, which is how
 * the Issue 092 refresh captured black/partial frames despite green
 * assertions (see docs/notes/2026-07-07-viewer-compressed-assets-and-baked-
 * market-visuals.md). This component watches R3F's demand-render loop and,
 * once the renderer has issued draw calls across a small run of consecutive
 * frames, stamps `data-viewer-frame-ready="1"` on the canvas element.
 *
 * The DOM attribute remains the test seam; `onReady` lets production remove
 * its loading overlay from the exact same truthful signal. It does NOT pin the
 * demand loop — after the signal fires it stops doing per-frame work (an
 * early-return guard), so `frameloop="demand"` still governs real rendering.
 * On WebGL context loss the marker is cleared and the frame counter resets, so
 * the spec can abort a capture that raced a reload/context-loss instead of
 * screenshotting a torn buffer.
 */

const FRAME_READY_ATTR = "data-viewer-frame-ready";
// Number of consecutive frames with real draw calls required before we
// consider the scene composited. One frame is not enough: the first
// invalidate() often renders before async textures/HDRI resolve, so we
// wait for the render pipeline to settle across a few frames.
const REQUIRED_PAINTED_FRAMES = 4;
// Defense-in-depth cap on the pre-signal invalidate loop. A scene that
// never paints (e.g. a permanently failed WebGL context) must not keep
// requesting frames forever. Once we've polled this many frames without
// earning the signal, we stop nudging the demand loop and let it go idle —
// the spec's frame-ready timeout then fails the test, which is the correct
// outcome for a never-painting scene. The cap is generous (well above the
// handful of frames a healthy cold start needs) so it never trips a real
// slow-but-eventually-painting mount.
const MAX_PRESIGNAL_FRAMES = 600;
// How long we will wait for texture work before giving up and signalling
// anyway. This gets its OWN budget rather than drawing on
// MAX_PRESIGNAL_FRAMES: waiting frames are not failing frames, and charging
// them to the paint budget starved a cold intl run into never signalling at
// all — a 45 s frame-ready timeout instead of a slightly-untextured capture.
//
// Measured in milliseconds, NOT frames. A frame budget is a wall-clock budget
// divided by an unknown refresh rate: 900 frames is 6 s at 144 Hz but over a
// minute at 15 fps, so the same constant either gives up too early on a fast
// display or outlives the spec's 45 s frame-ready timeout on a slow one —
// re-creating the hang this budget exists to prevent. The deadline sits
// comfortably under that timeout so exceeding it degrades to a capture,
// never to a hang.
const MAX_TEXTURE_WAIT_MS = 20_000;

export function ViewerFrameReadySignal({ onReady }: { onReady?: () => void }) {
	const gl = useThree((state) => state.gl);
	const invalidate = useThree((state) => state.invalidate);
	const paintedFramesRef = useRef(0);
	const polledFramesRef = useRef(0);
	const textureWaitStartedAtRef = useRef<number | null>(null);
	const signalledRef = useRef(false);

	useEffect(() => {
		const canvas = gl.domElement;
		const clearSignal = () => {
			signalledRef.current = false;
			paintedFramesRef.current = 0;
			// A recovered context gets a fresh polling budget so a legitimate
			// restore isn't starved by a prior run's exhausted cap.
			polledFramesRef.current = 0;
			textureWaitStartedAtRef.current = null;
			canvas.removeAttribute(FRAME_READY_ATTR);
			// A cleared signal must be re-earned; nudge the demand loop so
			// the frame counter can start ticking again after recovery.
			invalidate();
		};
		canvas.addEventListener("webglcontextlost", clearSignal);
		return () => {
			canvas.removeEventListener("webglcontextlost", clearSignal);
			canvas.removeAttribute(FRAME_READY_ATTR);
		};
	}, [gl, invalidate]);

	useFrame(() => {
		if (signalledRef.current) return;

		// A painted frame is not a COMPLETE frame while textures are still
		// resolving. Materials whose jpeg fallback is skipped in favour of a
		// ktx2 variant render with no map at all until the transcode lands, so
		// without this the harness captures flat-tinted cladding and benches.
		// Checked before the paint budget so waiting never consumes it.
		if (hasPendingTextureWork()) {
			textureWaitStartedAtRef.current ??= performance.now();
			if (
				performance.now() - textureWaitStartedAtRef.current <
				MAX_TEXTURE_WAIT_MS
			) {
				paintedFramesRef.current = 0;
				invalidate();
				return;
			}
		}

		// Stop polling a scene that has exceeded its frame budget without
		// ever compositing; don't invalidate again, letting the demand loop
		// idle instead of spinning forever.
		if (polledFramesRef.current >= MAX_PRESIGNAL_FRAMES) return;
		polledFramesRef.current += 1;

		// gl.info.render.calls is the number of draw calls issued in the
		// last rendered frame. Zero means nothing was painted (e.g. the
		// scene hasn't mounted geometry yet), so it doesn't count toward
		// readiness.
		if (gl.info.render.calls > 0) {
			paintedFramesRef.current += 1;
		} else {
			paintedFramesRef.current = 0;
		}

		if (paintedFramesRef.current >= REQUIRED_PAINTED_FRAMES) {
			signalledRef.current = true;
			gl.domElement.setAttribute(FRAME_READY_ATTR, "1");
			onReady?.();
			return;
		}

		// Keep the demand loop alive only until we've observed enough
		// painted frames; once signalled we stop requesting frames.
		invalidate();
	});

	return null;
}
